import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../components/toast-container";
import { createEGiftCard as createEGiftCardRequest } from "../api/eGiftCard.api";
import type { CreateEGiftCardPayload } from "../types/eGiftCard.types";
import type { CreateEGiftCardRequest } from "../types/eGiftCardForm.types";
import { toIsoStringWithOffset } from "../utils/eGiftCardDateTime";
import { toEGiftCardCreateItems } from "../utils/eGiftCardRequestMapper";
import { ApiResultError } from "../../../lib/result-codes";

interface UseCreateEGiftCardReturn {
  createEGiftCard: (data: CreateEGiftCardRequest) => Promise<boolean>;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
}

export const useCreateEGiftCard = (): UseCreateEGiftCardReturn => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createEGiftCard = useCallback(
    async (data: CreateEGiftCardRequest): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const request: CreateEGiftCardPayload = {
          merchant_brand_id: data.merchant_brand_id,
          gift_cards: toEGiftCardCreateItems(data.gift_cards),
          valid_from: toIsoStringWithOffset(data.valid_from, data.timezone),
          valid_to: toIsoStringWithOffset(data.valid_to, data.timezone),
          timezone: data.timezone,
          is_alert_low_stock: data.is_alert_low_stock,
        };

        const response = await createEGiftCardRequest(request);

        if (!response.success) {
          const message = response.message || "Failed to create gift card reward";
          setError(message);
          addToast({
            type: "warning",
            title: "Warning!",
            message,
          });
          return false;
        }

        addToast({
          type: "success",
          title: "Success!",
          message: "Gift card reward has been created successfully",
        });
        navigate("/e-gift-card");
        return true;
      } catch (caughtError: unknown) {
        console.error("Error creating gift card:", caughtError);
        const isInsufficientFund =
          caughtError instanceof ApiResultError &&
          (caughtError.code === 5 ||
            caughtError.code === 409 ||
            caughtError.response?.errors?.some(
              (apiError) => apiError.code === "insufficient_fund",
            ));
        const message = isInsufficientFund
          ? t("createEGiftCard.insufficientFund")
          : caughtError instanceof ApiResultError
            ? caughtError.message
            : "An error occurred while creating the gift card reward";
        setError(message);
        addToast({
          type: "error",
          title: "Error!",
          message,
        });
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [addToast, navigate, t],
  );

  return {
    createEGiftCard,
    isLoading,
    isError: error !== null,
    error,
  };
};
